import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { scaleImage } from '@nykaa/utils/image';
import styled from 'App/styled';

import NykaaLogoIcon from 'components/Icons/NykaaLogo';
import NykaaManLogoIcon from 'components/Icons/NykaaManLogo';
import { LocationAddressDetails } from '@nykaa/ui-factory/dist/AddressBar/components';
import { LocationDrawer } from '@nykaa/map-picker';
import { StoreOptions } from 'store/app/types';
import { State } from 'store/types';
import { areStoresAvailable } from 'helpers/utils/store';
import { platformNames, PIN_CODE_FOR_DELIVERY } from 'shared/constants';
import { getCookie } from 'shared/helpers/utils/cookie';
import {
  useIsConfigHeader,
  useIsThemeHeader,
  useIsAddressBarEnabled,
  useMapPickerVariant,
} from 'hooks/header';
import {
  LS_DELIVERY_LOCALITY,
  LS_DELIVERY_ADDRESS,
  ROUTE_SELECT_DELIVERY_LOCATION,
} from 'shared/pages/SelectDeliveryLocation/constants';

const Wrapper = styled.a`
  max-width: 120px;
  ${(props: any) => (props.shouldAddPadding ? 'padding-left: 28px;' : '')}

  svg rect {
    fill: ${({ color }) => color};
  }

  svg {
    vertical-align: middle;
  }

  img {
    height: 24px;
  }
`;
interface Props {
  storeId: string;
  storeOptions: StoreOptions;
  shouldAddPadding?: boolean;
}

function readAddressData() {
  if (__SERVER__) return { pincode: '', locality: '', address: '' };
  const pin = getCookie(PIN_CODE_FOR_DELIVERY)?.toString() || '';
  let locality = '';
  let address = '';
  try {
    locality = localStorage.getItem(LS_DELIVERY_LOCALITY) || '';
    address = localStorage.getItem(LS_DELIVERY_ADDRESS) || '';
  } catch {
    // localStorage unavailable
  }
  return { pincode: pin, locality, address };
}

function View({ storeId, storeOptions, shouldAddPadding }: Props) {
  const { stores } = storeOptions;
  const isThemeHeader = useIsThemeHeader();
  const { headerTextColor } = useIsConfigHeader();
  const history = useHistory();
  const isAddressBarEnabled = useIsAddressBarEnabled();
  const mapPickerVariant = useMapPickerVariant();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const mapPickerConfig = useSelector(
    (state: State) => state.remoteConfig.configs?.mapPickerConfig
  );
  const {
    defaultAddressTitle,
    defaultAddressSubtitle,
    searchPlaceholder,
    useCurrentLocationLabel,
  } = mapPickerConfig || {};

  const ssrPincode = useSelector(
    (state: State) => state.auth.user.pinCode ?? ''
  );

  const [addressData, setAddressData] = useState(() => readAddressData());
  const [isAddressBarStorageSynced, setIsAddressBarStorageSynced] =
    useState(false);

  useEffect(() => {
    const refresh = () => {
      setAddressData(readAddressData());
      setIsAddressBarStorageSynced(true);
    };

    refresh();
    //Future Purpose if we implement address change
    //listener using events or any other method, we can
    // call refresh to update the address in header without page reload

    // window.addEventListener(DELIVERY_ADDRESS_CHANGED_EVENT, refresh);

    // const handlePageShow = (e: PageTransitionEvent) => {
    //   if (e.persisted) {
    //     refresh();
    //   }
    // };
    // window.addEventListener('pageshow', handlePageShow);

    return () => {
      // window.removeEventListener(DELIVERY_ADDRESS_CHANGED_EVENT, refresh);
      // window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const {
    pincode: clientPincode,
    locality: deliveryLocality,
    address: deliveryAddress,
  } = addressData;

  const pincode = !__SERVER__ ? clientPincode : ssrPincode;

  const handleAddressClick = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const handleSearchClick = useCallback(() => {
    setIsDrawerOpen(false);
    history.push(ROUTE_SELECT_DELIVERY_LOCATION, { screen: 'search' });
  }, [history]);

  const handleUseLocationClick = useCallback(
    (coords: { lat: number; lng: number }) => {
      setIsDrawerOpen(false);
      history.push(ROUTE_SELECT_DELIVERY_LOCATION, {
        lat: coords.lat,
        lng: coords.lng,
      });
    },
    [history]
  );

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  if (areStoresAvailable(storeOptions)) {
    const [selectedStore] = stores.filter((store) => store.storeId === storeId);

    if (selectedStore) {
      const { storeLogo } = selectedStore;
      const scaledLogo = scaleImage({
        url: storeLogo,
        height: 24,
      });

      return (
        <Wrapper
          href="/?root=logo"
          className="logo"
          aria-label="Nykaa Logo"
          shouldAddPadding={shouldAddPadding}
        >
          <img
            src={scaledLogo}
            className="logo-img"
            alt="brand-logo"
          />
        </Wrapper>
      );
    }
  }

  if (!isAddressBarEnabled) {
    return (
      <Wrapper
        href="/?root=logo"
        className="logo"
        aria-label="Nykaa Logo"
        shouldAddPadding={shouldAddPadding}
      >
        {__PLATFORM__ !== platformNames.NYKAA ? (
          <NykaaManLogoIcon
            isLight={isThemeHeader}
            color={headerTextColor}
          />
        ) : (
          <NykaaLogoIcon isLight={isThemeHeader} />
        )}
      </Wrapper>
    );
  }

  return (
    <>
      <LocationAddressDetails
        state={deliveryLocality?.toUpperCase()}
        fullAddress={deliveryAddress}
        pincode={pincode}
        defaultTitle={defaultAddressTitle}
        defaultSubtitle={defaultAddressSubtitle}
        onClick={handleAddressClick}
        isLoading={!isAddressBarStorageSynced}
      />
      {isDrawerOpen &&
        !__SERVER__ &&
        createPortal(
          <LocationDrawer
            onSearchClick={handleSearchClick}
            onUseLocationClick={handleUseLocationClick}
            onClose={handleDrawerClose}
            searchPlaceholder={searchPlaceholder}
            useCurrentLocationLabel={useCurrentLocationLabel}
            enableLegacyGeolocation={mapPickerVariant === 'B'}
          />,
          document.body
        )}
    </>
  );
}

export default View;
